/**
 * CreatePost Page — Clean, minimal post creator.
 * 3 media sources: Gallery (file), Media (picker with tabs), Thread.
 * Accepts pre-selected media from Gallery page via location.state.
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { X, Images, Loader2, Cloud, Users, GitBranch } from 'lucide-react'
import api from '../services/api'
import Avatar from '../components/ui/Avatar'
import MediaPickerModal from '../components/MediaPickerModal'
import type { SelectedMedia } from '../components/MediaPickerModal'

export default function CreatePost() {
  const navigate = useNavigate()
  const location = useLocation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [text, setText] = useState('')
  const [mediaFile, setMediaFile] = useState<File | null>(null)
  const [mediaPreview, setMediaPreview] = useState<string | null>(null)
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null)
  const [isPosting, setIsPosting] = useState(false)
  const [showMediaPicker, setShowMediaPicker] = useState(false)

  const userProfile = JSON.parse(localStorage.getItem('user_profile') || '{}')
  const user = {
    name: userProfile.name || 'User',
    username: userProfile.username || 'user',
    avatar: userProfile.avatar,
  }

  const charLimit = 400
  const charsRemaining = charLimit - text.length

  // Accept pre-selected media from Gallery page
  useEffect(() => {
    const state = location.state as any
    if (state?.mediaUrl) {
      setMediaPreview(state.mediaUrl)
      setMediaType(state.mediaType === 'video' ? 'video' : 'image')
    }
  }, [])

  const handleMediaPickerSelect = (media: SelectedMedia | SelectedMedia[]) => {
    const m = Array.isArray(media) ? media[0] : media
    if (m) {
      setMediaPreview(m.thumbnail || m.url)
      setMediaType(m.type)
    }
    setShowMediaPicker(false)
  }

  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) { alert('File size must be less than 50MB'); return }
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')
    if (!isImage && !isVideo) { alert('Please select an image or video file'); return }

    setMediaFile(file)
    setMediaType(isImage ? 'image' : 'video')
    const reader = new FileReader()
    reader.onload = (event) => { if (event.target?.result) setMediaPreview(event.target.result as string) }
    reader.readAsDataURL(file)
  }

  const handleRemoveMedia = () => {
    setMediaFile(null)
    setMediaPreview(null)
    setMediaType(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handlePost = async () => {
    if (!text.trim() && !mediaFile && !mediaPreview) { alert('Please add some text or media'); return }
    try {
      setIsPosting(true)
      const result = await api.createPost({
        text: text.trim(),
        mediaFile: mediaFile || undefined,
        mediaType: mediaType || undefined,
      })
      if (result.success) navigate('/')
      else alert(result.message || 'Failed to create post')
    } catch { alert('Failed to create post') }
    finally { setIsPosting(false) }
  }

  const canPost = (text.trim() || mediaFile || mediaPreview) && !isPosting

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full" disabled={isPosting}>
            <X className="w-5 h-5 text-gray-700" />
          </button>
          <button onClick={handlePost} disabled={!canPost}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              canPost ? 'bg-onrecord-orange text-white hover:bg-onrecord-orange-dark' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}>
            {isPosting ? <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Posting...</div> : 'Post'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-4">
        <div className="flex gap-3 mb-4">
          <Avatar src={user.avatar} alt={user.name} size="lg" />
          <div className="flex-1">
            <div className="font-semibold text-gray-900">{user.name}</div>
            <div className="text-sm text-gray-500">@{user.username}</div>
          </div>
        </div>

        <div className="mb-4">
          <textarea value={text} onChange={(e) => { if (e.target.value.length <= charLimit) setText(e.target.value) }}
            placeholder="What's happening?" className="w-full text-xl resize-none outline-none placeholder-gray-400"
            rows={6} disabled={isPosting} autoFocus />
          <div className="flex justify-end mt-2">
            <div className={`text-sm font-medium ${charsRemaining < 20 ? 'text-red-500' : charsRemaining < 50 ? 'text-orange-500' : 'text-gray-400'}`}>
              {text.length}/{charLimit}
            </div>
          </div>
        </div>

        {/* Media Preview */}
        {mediaPreview && (
          <div className="relative mb-4 rounded-2xl overflow-hidden border border-gray-200">
            {mediaType === 'image'
              ? <img src={mediaPreview} alt="Preview" className="w-full max-h-96 object-contain bg-gray-50" />
              : <video src={mediaPreview} controls className="w-full max-h-96 object-contain bg-gray-50" />
            }
            <button onClick={handleRemoveMedia} className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-full" disabled={isPosting}>
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        )}

        {/* Media Actions — 3 buttons */}
        {!mediaPreview && (
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2">
              <button onClick={() => fileInputRef.current?.click()} disabled={isPosting}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors">
                <Images className="w-5 h-5 text-onrecord-orange" />
                <span className="text-sm font-medium text-gray-700">Gallery</span>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={handleMediaSelect} className="hidden" />

              <button onClick={() => setShowMediaPicker(true)} disabled={isPosting}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors">
                <Cloud className="w-5 h-5 text-onrecord-orange" />
                <span className="text-sm font-medium text-gray-700">Private</span>
              </button>

              <button onClick={() => setShowMediaPicker(true)} disabled={isPosting}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors">
                <Users className="w-5 h-5 text-onrecord-orange" />
                <span className="text-sm font-medium text-gray-700">Shared</span>
              </button>

              <button onClick={() => navigate('/create-thread')} disabled={isPosting}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors">
                <GitBranch className="w-5 h-5 text-onrecord-orange" />
                <span className="text-sm font-medium text-gray-700">Thread</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Upload from gallery, select from private/shared media, or create a thread</p>
          </div>
        )}
      </div>

      {/* Tabbed Media Picker — Private + Shared in one modal */}
      <MediaPickerModal
        isOpen={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleMediaPickerSelect}
      />
    </div>
  )
}
